import type { Secret } from '@imprivata-cloud/dataprivacy';
import type { ConsentGrantType, ConsentType } from './constants';

export type Metadata = {
  rawMetadata?: string;
  metadataUrl?: string;
};

export type TenantInfo = { tenantId: string; tenantType: string };

export interface IdpMetadataGetResponse {
  idpMetadataInfoList: IdpMetadataInfo[];
  domainNames: string[];
}

export interface IdpMetadataInfo {
  idpMetadataId: string;
  idpId: string;
  idpDisplayName: string;
  metadataUrl?: string;
}

export interface IdpMetadataSaveRequest {
  metadataId?: string;
  displayName: string;
  domain: string;
  metadata?: Metadata;
}

export interface MetadataSaveResponse {
  entityId: string;
  metadataId: string;
}

// IdpConfigGet
export interface IdpConfigGetResponse {
  idpConfigId: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}
export interface IdpConfigGetRequest {
  idpMetadataId: string;
}

// IdpConfigsFind
export interface IdpConfigsFindRequest {
  idpMetadataId?: string;
}
export interface IdpConfigsFindResponse {
  idpConfigs: IdpConfig[];
}

export interface IdpConfig {
  idpConfigId: string;
  idpMetadataId: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}

// IdpConfigSave
export interface IdpConfigSaveRequest {
  idpMetadataId: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}
export interface IdpConfigSaveResponse {
  idpConfigId: string;
}

// IdpConfigUpdate
export interface IdpConfigUpdateRequest {
  idpMetadataId?: string;
  idpConfigId?: string;
  samlGroupAttrName: string;
  samlGroupAttrValues: string[];
}
export interface IdpConfigUpdateResponse {
  idpConfigId: string;
}

export interface OrgInfoGetResponse {
  logoUrl: string;
  orgName: string;
  items: string;
  businessEmail: string;
}

// All fields are optional and only the fields that are provided will be updated.
export interface OrgInfoSaveRequest {
  logoUrl?: string;
  orgName?: string;
  businessEmail?: string;
}

export interface OrgPreferencesGetResponse extends OrgPreferencesUpdateRequest {}

export interface OrgPreferencesUpdateRequest {
  logoUrl?: string;
  orgName?: string;
  usernameLabel?: string;
  passwordLabel?: string;
  proxCardLabel?: string;
}

export interface GetAadConsentRequest {
  aadTenantId: string;
}

export interface GetAadConsentResponse {
  aadConsentUrl: string;
}

export interface TenantSetupCompleteRequest {
  email: string;
  withEntraId: boolean;
  consent: ApiConsent;
}

export interface ApiConsent {
  grantType?: string; // setup rerun may not need DPA consent
  documentName: string;
}

export interface TenantLookupResponse {
  tenantIds: string[];
  tenantList: TenantInfo[];
}

export interface GetClientUserSessionResponse {
  tenantId?: string;
  tenantType?: string;
  userInfo?: UserInfo;
}

export type UserInfo = {
  username: string;
  email?: string;
  roleType: string;
  productExperienceVisitorId?: string;
  userUpn?: string;
  userDisplayName?: string;
};

export interface EntraIdConsentUrlGetResponse {
  entraConsentUrl: string;
}

export interface EntraIdConsentUrlGetRequest {
  entraTenantId: string;
}

export interface EntraIdTenantSetupResponse {
  entraUserId: string;
}

export type OrgInfo = {
  name: string;
  logoUrl: string;
};

export type StartSsoLink = {
  startSsoUrl: string;
  applianceDesignator: string;
};

export type PurchasedProduct = {
  displayName: string;
  integrationId: string;
  productTag: string;
  spDisplayName: string;
  spMetadataId: string;
  startSsoLinks: StartSsoLink[];
};

export type NonPurchasedProduct = {
  displayName: string;
  productTag: string;
  learnMoreUrl: string;
};

export type ProductInfo = {
  purchasedProducts: PurchasedProduct[];
  nonPurchasedProducts: NonPurchasedProduct[];
};

export interface GetLandingPageResponse {
  orgInfo?: OrgInfo;
  productInfo: ProductInfo;
}

export enum FileAccessModifier {
  PRIVATE = 'private',
  PUBLIC = 'public',
}

export type PrepareFileUploadResponse = {
  uploadUrl: string;
  readUrl: string;
};

// DPA
export interface CheckDpaConsentRequest {
  consentDocumentName: string;
}
export interface CheckDpaConsentResponse {
  actionType: CheckDpaConsentResponseActionType;
  documentName: string; // example impr-dpa-consent-v2_0.html
  documentUrl: string; // example https://file-access.app.dev.imprivata.cloud/documents/consent/impr-dpa-consent-v2_0.html
}

export enum CheckDpaConsentResponseActionType {
  NONE = 'NONE', // Don't show the clickthrough
  CHECK_NEEDED = 'CHECK_NEEDED',
  UPDATE_NEEDED = 'UPDATE_NEEDED',
}

// create token
export interface ClientCreateRequest {
  enterpriseId: string;
  installationTokenExpirationInMinutes: number;
}

export interface ClientCreateResponse {
  clientMetadata: ClientMetadata;
}

export type ClientMetadata = {
  clientId: string;
  eamEnterpriseId: string;
  installationToken: TokenSecret;
  tokenExpiryTime: number;
  createdAt: number;
  installationTokenValue: string;
};
export type TokenSecret = {
  data: string;
  origin: null;
  isProxied: false;
};

export type GroupRequest = {
  groupName: string;
  groupId: string;
  userCount: number;
  syncEnabled: boolean;
  adminEnabled: boolean;
  idpDeleted: boolean;
};

export type FindDirectoriesResponse = {
  directories: Directory[];
};

export type Directory = {
  name: string;
  directoryId: string;
  latestSyncAt: string;
  syncedGroupCount: number;
  idpTenantId: string;
};

export type ListSelectors = {
  fields?: string[];
  sortFields?: SortField[];
  filters?: Filter[];
  search?: SearchSelector;
};

export type SortField<T = string> = {
  field: T;
  order?: SortOrder;
};

export type Filter = {
  field: string;
  value?: string | boolean | number;
  operator?: FilterOperator;
};

export type SearchSelector<T = string> = {
  pattern: string;
  fields?: T[];
};

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export enum FilterOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not-equals',
  NOT_NULL = 'not-null',
}

export const ALL_ITEMS_PATTERN = '*';

export type FindUsersRequest =
  | {
      selectors: ListSelectors;
      pageSize?: number;
    }
  | {
      pageToken: string;
      pageSize?: number;
    };

export type DirectorySyncProgressGetRequest = {
  directoryId: string;
};

export type DirectorySyncProgressGetResponse = {
  syncStatus: SyncStatus;
  failureReason?: SyncFailureReason;
  info?: SyncProgressInfo;
  latestSyncAt?: string;
};

export enum SyncStatus {
  NOT_INITIALIZED = 'not-initialized',
  RUNNING = 'running',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
}

export enum SyncFailureReason {
  IDP_FAILURE = 'idp-failure',
  SYNC_ALREADY_RUNNING = 'sync-already-running',
  SYNC_INTERRUPTED = 'sync-interrupted',
  SYSTEM_FAILURE = 'system-failure',
  TRANSIENT_IDP_FAILURE = 'transient-idp-failure',
  TRANSIENT_SYSTEM_FAILURE = 'transient-system-failure',
}

export type SyncProgressInfo = {
  directoryId: string;
  syncId: string;
  totalStepsProcessed: number;
  totalSteps: number;
};

export type DirectoryFailedSyncFindRequest = {
  directoryId: string;
};

export type DirectoriesFailedSyncFindResponse = {
  info: FailedSyncsFindInfo[];
};

export type FailedSyncsFindInfo = {
  syncId: string;
  failureReason: string;
  createdAt: string;
  endedAt?: string;
};

export type FindUsersResponse = {
  users: User[];
  totalCount: number;
  latestUpdatedAt: string;
  pagingInfo?: PagingInfo;
};

export type User = {
  userId: string;
  displayName: string;
  upn: string;
  tenantId: string;
  idpTenantId: string;
  idpUserId: string;
  roleType: string;
  firstName?: string;
  lastName?: string;
  mobilePhone?: string;
  email?: string;
  lockedUntil?: string; // ISO 8601 date-time
  idpUserEnabled: boolean;
  createdAt: string; // ISO 8601 date-time
  updatedAt: string; // ISO 8601 date-time
};

export type PagingInfo = {
  afterToken: string;
  beforeToken: string;
};

export interface GroupForSyncMarkRequest {
  groupId: string;
  isAdmin: boolean;
}

export interface GroupForSyncUnmarkRequest {
  groupId: string;
}

export interface GroupsFindResponse {
  groups: Group[];
  totalCount: number;
  latestUpdatedAt: string;
  pagingInfo: PagingInfo;
}

export interface Group {
  groupName: string;
  groupId: string;
  userCount: number;
  syncEnabled: boolean;
  adminEnabled: boolean;
  idpDeleted: boolean;
}

export interface FindListRequest {
  selectors: {
    search?: {
      pattern: string;
      fields: string[];
    };
    filters: {
      field: string;
      value: string;
      operator: string;
    }[];
  };
}

export interface DomainsValidateRequest {
  names: string[];
}

export interface AuthenticatorsFindByUserIdsRequest {
  userIds: string[];
}

export interface AuthenticatorsFindByUserIdsResponse {
  authenticators: Authenticator[];
}

export interface AuthenticatorsUnenrollRequest {
  authenticatorIds: string[];
}

export interface Authenticator {
  authenticatorId: string;
  userId: string;
  shortName: string;
  lastUsedAt: string;
  factorType: string;
  assuranceType: string;
}
export interface ConsentInfo {
  actionType: CheckDpaConsentResponseActionType;
  consentType?: ConsentType; // @deprecated v1
  grantType: ConsentGrantType;
  documentMetadataList: Pick<ConsentDocumentMetadata, 'documentName' | 'documentPurpose' | 'documentSource'>[]; // v2
  residenceState?: string; // @deprecated v1
  documentName?: string; // @deprecated v1
  documentUrl?: string; // @deprecated v1
}
export interface SelfServiceLandingPageDataResponse {
  orgInfo: {
    name: string;
    logoUrl: string;
  };
  consentInfos: ConsentInfo[];
  authenticators: Authenticator[];
  userInfo: {
    userId?: string;
    directoryId?: string;
    username?: string;
    externalId?: string;
  };
  residenceLocations?: ResidenceLocation[];
}

export interface ConsentSubmitResponse {
  grantType: ConsentGrantType;
  documentName: string;
  residenceState?: string;
  consentType: ConsentType;
  userId?: string;
}

export interface ConsentSubmitRequest {
  consentType: ConsentType;
  grantType: ConsentGrantType;
  documentName: string;
  userId?: string;
  residenceState?: string;
}

export interface TenantInfoResponse {
  domainTenants: TenantInfo[];
}

export interface SpDisplayNameUpdateRequest {
  spMetadataId: string;
  spDisplayName: string;
}

export interface SpDisplayNameUpdateResponse {
  spMetadataId: string;
}

export type ImprDomain = {
  domainName: string;
  isPrimary: boolean;
};

export type ImprDir = {
  directoryId?: string;
  name?: string;
  description?: string;
  type?: number;
  domains: ImprDomain[];
};

export interface ImprUserSaveRequest {
  user: ImprUser;
}

export interface ImprUserSaveResponse {
  userId: string;
}

export type ImprUserDeleteRequest = {
  userId: string;
};

export interface ImprDirResponse {
  directories: ImprDir[];
}

export type ImprUserRole = {
  roleId?: string;
  value: ImprUserRoleValue;
  displayName?: string;
  action?: Action;
  isPrimary?: boolean;
};

export enum ImprUserRoleValue {
  admin = 'admin',
  user = 'user',
}

export type GrpMembership = {
  groupId: string;
  displayName?: string;
};

export type ImprUser = {
  userId?: string;
  directoryId?: string;
  username: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  userType?: string;
  roles?: ImprUserRole[];
  userGroupMembership?: GrpMembership[];
  emails: ImprUserEmail[];
  phoneNumbers?: ImprPhoneNumber[];
  lockedUntil?: string;
};

export function findRole(role: ImprUserRoleValue, roles: ImprUserRole[] | undefined) {
  return roles?.find((r) => r.value === role);
}

export function isAdmin(user: ImprUser | undefined) {
  return !!findRole(ImprUserRoleValue.admin, user?.roles);
}

export enum Action {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

export const addAdminRoleTemplate: ImprUserRole = {
  action: Action.ADD,
  value: ImprUserRoleValue.admin,
  displayName: '',
  isPrimary: true,
};

export function buildAddUserRole(isPrimary: boolean): ImprUserRole {
  return {
    action: Action.ADD,
    value: ImprUserRoleValue.user,
    displayName: '',
    isPrimary,
  };
}

export type ImprUserEmail = {
  emailId?: string;
  value: string;
  isPrimary: boolean;
  action?: Action;
};

export type ImprPhoneNumber = {
  value: string;
  phoneNumberId?: string;
  type?: string;
  displayName?: string;
  isPrimary?: boolean;
  action?: Action;
};

export type PageInfo = {
  count: number;
  startIndex: number;
};

export interface ImprIdpUsersFindRequest {
  pageInfo?: PageInfo;
}

export interface ImprIdpUsersFindResponse {
  usersTotal: number;
  users: ImprUser[];
  pageInfo: PageInfo;
}

export interface IdpGetUserRequest {
  userId: string;
}

export interface IdpGetUserResponse {
  user: ImprUser;
}

export type InitialUser = {
  username: string;
  password: Secret;
};

export interface DirectoryAddRequest {
  directory: ImprDir;
  initialUser?: InitialUser;
}

export interface InstallationToken {
  data: string;
}
export interface ClientCredential {
  clientCredentialId: string;
  clientMetadata?: ClientMetadata;
  installationToken: InstallationToken;
  installationTokenExpiry: number;
  createdAt: number;
  integrationId?: string;
  installationTokenValue: string;
}

export enum ClientCredentialClientType {
  PAS = 'pas',
  AIP = 'aip',
}

export interface CreateClientCredentialRequest {
  clientType: ClientCredentialClientType;
  clientMetadata?: ClientMetadata;
  installationTokenExpirationInMinutes: number;
  accessTokenExpirationInMinutes: number;
  integrationId?: string;
}

export interface CreateClientCredentialResponse {
  clientCredential: ClientCredential;
}

export type ImprDirectory = {
  name?: string;
  description?: string;
  directoryId?: string;
  type?: string;
  domains?: ImprDomain[];
};

export type ImprDirectoriesConfig = {
  cidrNotations: string[];
  imprDomainSuffix: string; // e.g. imprivata.id, dev.imprivata.id
};

export type ImprIdpDataGetResponse = {
  directories: ImprDirectory[];
  directoriesConfig: ImprDirectoriesConfig;
};

export type UserPasswordChangeRequest = {
  newPassword: Secret;
};
export enum DocumentPurpose {
  CONSENT = 'biometric-consent',
  NOTICE = 'biometric-notice',
}
export interface PrepareUploadRequest {
  filename: string;
  purpose: DocumentPurpose;
}

export interface PrepareUploadRespone {
  readUrl: string;
  uploadUrl: string;
  operationId: string;
}

export interface ResidenceLocationsSaveRequest {
  locations: string[];
}

export interface ResidenceLocationsSaveResponse {
  savedCount: number;
}
interface CustomerAgreementInfo {
  documentFilename: string;
  readUrl: string;
  documentPurpose: DocumentPurpose;
  documentSource: 'imprivata' | 'customer';
  requiredResidenceLocations: string[];
}
export interface ResidenceLocation {
  shortName: string;
  fullName: string;
  displayName: string;
}

export interface ConsentDocumentMetadata {
  documentName: string;
  readUrl: string;
  documentPurpose: 'biometric-consent' | 'biometric-notice';
  documentSource: 'customer' | 'imprivata';
  requiredResidenceLocations: ResidenceLocation[];
}

export interface UserConsentCheckRequest {
  userId?: string;
  userUpn?: string;
  consentType: string;
}

export interface UserConsentCheckResponse {
  actionType: string;
  grantType: string;
  documentMetadata: ConsentDocumentMetadata[];
}

export interface UserConsentSubmitRequest {
  consent: {
    consentType: ConsentType;
    userId: string;
    residenceLocationShortName: string;
    grantType: string;
    documentNameList: string[];
  };
  userUpn?: string;
  userDisplayName?: string;
}

export interface CustomerAgreementInfoGetResponse {
  customerAgreementInfoList: CustomerAgreementInfo[];
  residenceLocationList: ResidenceLocation[];
  customAgreementEnabled: boolean;
  customerMandatoryResidenceLocationList: ResidenceLocation[];
}

export interface LegalAgreementDeleteRequest {
  documentPurpose: DocumentPurpose;
}

type ConsentRevoke = Pick<ConsentDocumentMetadata, 'documentName' | 'documentPurpose' | 'documentSource'>;

export interface UserConsentRevokeRequest {
  consentRevokeList: ConsentRevoke[];
}

export type TempCodeAssignRequest = {
  userId: string;
  data?: Record<string, unknown>;
};

export type TemporaryCode = {
  data: string;
  origin?: string;
  isProxied?: boolean;
};

export type TempCodeAssignResponse = {
  tempCode: TemporaryCode;
};

export type TemporaryCodeMetadataGetRequest = {
  userId: string;
};

export type TemporaryCodeMetadataGetResponse = {
  userId: string;
  username: string;
  multiUse: boolean;
  createdAt: string;
  startsAt: string;
  expiresAt: string;
  authenticatorId: string;
};

export type UserUnlockRequest = {
  userId: string;
};
